import config from '@/config'
import router from '@/router'
import {
  ROUTE_NAMES,
} from '@/constant/routes'
import { workspaceBackRoute, WORKSPACE_APP_ID, WORKSPACE_FROM_QUERY } from '@/constant/workspaceBack'
// AHA-47221: the add-in's editor entrance — /ppt-addin/edit-slides.
import { isPptAddinEditorRoute } from '@/business-logic/ppt-addin-editor-routes'
import { isPptDialog } from '@/business-logic/ppt-dialog'
import { EDITOR_LAST_DESTINATION } from '@/constant/storageKeys'

// AHA-47221. One spelling of the key: backAction gates two separate decisions on this flag —
// whether the editor route steps back, and whether Back hands over to backV4Action — and they
// have to switch together or it is not really one flag. Same pattern as AUTO_STOP_QUIZ_FLAG in
// ppt-addin.actions.js.
const PPT_NEW_UI_FLAG = 'featureToggles/canUsePptNewUi'

const SET_PREVIOUS_ROUTE = 'SET_PREVIOUS_ROUTE'
const SET_LAST_DESTINATION = 'SET_LAST_DESTINATION'

// AHA-46714: best-effort restore — unavailable storage or corrupt JSON reads
// as "nothing recorded". Only path is required: userId may be null and is
// validated at read time (see resolveBackDestination), never at module init,
// where currentUser has not loaded yet.
const readStoredLastDestination = () => {
  try {
    const raw = sessionStorage.getItem(EDITOR_LAST_DESTINATION)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && parsed.path ? { userId: parsed.userId ?? null, path: parsed.path } : null
  } catch (e) {
    return null
  }
}

// AHA-47324: sessionStorage is shared by every same-origin frame in the tab,
// so a nested copy of this app would overwrite the top window's Back target.
// The template-preview popup embeds /shared-template/:id, a marked
// destination, in a same-origin iframe; its recorder used to win, and Back
// after an editor reload landed on the preview page. Only the outermost
// same-origin instance may record. A cross-origin parent (MS Teams, Zoom and
// Google Slides host the whole app in an iframe) throws on the origin read:
// that is a normal top-level app, not a nested one, so it keeps recording.
const isNestedSameOriginFrame = () => {
  try {
    if (window.self === window.top) return false
    return window.parent.location.origin === window.location.origin
  } catch (e) {
    return false
  }
}

const _state = {
  previousRoute: null,
  isBackstageRightColumnCollapsed: localStorage.getItem('isBackstageRightColumnCollapsed') === 'true',
  // AHA-46714: what the editor Back arrow returns to — deliberately not the
  // browser history stack, whose contents the app does not control.
  lastDestination: readStoredLastDestination(),
}

const _getters = {
  isEnableAddinPowerpoint: (_, __, rootState) => rootState.isEnableAddinPowerpoint,
  isGuestUser: (_, __, rootState) => rootState.currentUser.isGuest,
  workingMode: (_, __, rootState) => rootState.workingMode,
  previousRoute: (state) => state.previousRoute,
  isDemoMode: (_, __, rootState) => rootState.isDemoMode,
  hasResizableSlideNotes: (_, getters, ___, rootGetters) => {
    if (rootGetters.isBackstage || getters.isEnableAddinPowerpoint) {
      return false
    }
    return rootGetters['featureToggles/isFeatureEnabled']('resizable-slide-notes')
  },
  isBackstageRightColumnCollapsed: (state) => state.isBackstageRightColumnCollapsed,
  lastDestination: (state) => state.lastDestination,
}

// Transitional routes are routes that are not true pages, but they are used to transition to other routes, so it it not comparable in the logic of backAction below
const TRANSITIONAL_ROUTES = [
  ROUTE_NAMES.DEMO_PRESENTATION,
  ROUTE_NAMES.AUTH,
]

const TRANSITIONAL_REFERER_URLS = [
  '/pages/register',
  '/pages/login',
  '/auth',
]

const isPreviousRouteTransitional = (routeName) => {
  return TRANSITIONAL_ROUTES.includes(routeName)
}

const isReferrerTransitional = (referrer) => {
  return TRANSITIONAL_REFERER_URLS.some((url) => {
    return referrer.includes(url)
  })
}

const isReferrerEmtpy = () => {
  const { referrer } = document
  if (isReferrerTransitional(referrer)) {
    return true
  }
  return referrer === '' || referrer === undefined || referrer === null
}

// AHA-46604: history.back() to the workspace is only safe when the referrer
// proves this document was actually opened BY the workspace app (its manifest
// origin). A forwarded/pasted ?from=workspace link, or the post-login redirect
// (referrer /pages/login — router.js keeps the query through the auth chain),
// must not walk history blindly: that back-traps through the login page or
// exits off-site.
const isWorkspaceReferrer = (referrer, workspaceBaseUrl) => {
  if (!referrer || !workspaceBaseUrl) return false
  try {
    return new URL(referrer).origin === new URL(workspaceBaseUrl).origin
  } catch (e) {
    return false
  }
}

// AHA-46604: resolve back for a workspace-launched editor (extracted so
// backV4Action stays inside the complexity budget).
//   • flag off / no manifest → the workspace app cannot render; keep the
//     legacy Presentations fallback (never a blank embed page);
//   • history.back() only under positive provenance — a history entry, a
//     non-empty non-transitional referrer, AND a referrer origin matching the
//     workspace manifest origin. document.referrer is a document-lifetime
//     value (it never changes on in-SPA navigation), so this is provenance of
//     the LOAD, not of the top history entry — after in-SPA drift a single
//     back step can stay inside the editor, which is why everything
//     uncertain falls through to the deterministic push;
//   • otherwise → push the workspace location the editor was opened from
//     (`fromPath`, folder / kind subtab), falling back to the My Files root —
//     the same named-route fallback shape as backV4Action's Case 2/3.
//
// Why back() still WINS when its guard holds, even though the push is more
// deterministic (Tam review): back() returns to the workspace app's actual
// prior history entry, restoring its in-app state — scroll position, folder
// selection, expansion — and keeps the browser Back/Forward stack coherent
// (no duplicate entry). The push re-mounts the embed fresh at the folder
// route: it always lands on the right FOLDER but loses that in-app state and
// adds a history entry. So back() is preferred for its fidelity in the common
// (direct-arrival) case, gated hard by the provenance checks; the in-SPA-drift
// mis-land is the accepted residual cost of that preference, not an oversight.
// Flipping the order (always push) is a deliberate UX trade left for a
// follow-up, not something to change silently here.
const backToWorkspace = (rootGetters, hasHistory, query) => {
  const workspaceApp = rootGetters['embedApp/manifestById'](WORKSPACE_APP_ID)
  if (!rootGetters['embedApp/isEnabled'] || !workspaceApp) {
    router.push({ name: ROUTE_NAMES.PRESENTATIONS })
    return
  }
  if (
    hasHistory &&
    !isReferrerEmtpy() &&
    isWorkspaceReferrer(document.referrer, workspaceApp.baseUrl)
  ) {
    window.history.back()
    return
  }
  // Deterministic path for when the provenance guard above does not hold — and
  // the strictly-correct-folder path when it can't (see the back()-vs-push
  // trade in the doc block above).
  router.push(workspaceBackRoute(query))
}

// AHA-46714: resolve where the editor Back arrow should land. Returns the
// recorded path when it is usable by the current user, else null (caller
// falls back to Presentations). A null userId means the value was recorded
// in this tab before the user profile loaded (same tab, same person);
// values recorded after load carry the id and stay strictly validated,
// which is what blocks cross-account inheritance of private folder paths.
// dest.path must also look like a genuine in-app absolute path — a tampered
// or malformed sessionStorage value (non-string, or a protocol-relative
// "//host" value that browsers treat as an absolute URL) must never reach
// router.push, so it is rejected here and falls back to Presentations too.
const resolveBackDestination = (dest, currentUserId) => {
  if (!dest || typeof dest.path !== 'string') return null
  if (!dest.path.startsWith('/') || dest.path.startsWith('//')) return null
  if (dest.userId != null && dest.userId !== currentUserId) return null
  return dest.path
}

const actions = {
  async backAction({ getters, dispatch, rootGetters }) {
    if (getters.isEnableAddinPowerpoint) {
      // AHA-47221: the add-in editor route is a detour INSIDE a bound frame, not a way out
      // of one. The user reached them from the canvas menu (or straight from the presentation
      // list) while still bound and, usually, still presenting; Back there means "leave the
      // detour", so it steps back to whatever pushed it and nothing else happens. In particular
      // the two clears below must NOT run: they exist to release a frame that is leaving its
      // presentation, and firing them here would unbind a frame the user is still presenting
      // from — the same reason the canvas menu unbinds nothing on the way in.
      //
      // history over a push because "previous" genuinely varies (presenting canvas, embed list),
      // and stepping back also restores that page's own state instead of re-mounting it. With no
      // entry to step back to there is nothing to return to, so it falls through to the flow
      // below rather than walking the frame off the pane.
      //
      // The route name only exists on add-in builds, and the flag check keeps the whole branch
      // out of the old path: flag off => everything below runs exactly as before.
      //
      // The DIALOG goes first, and it does not step back — it always lands on the list.
      //
      // A dialog is a fresh browser context opened straight at the editor URL, so there is no
      // "where I was" in it to step back to: its history is one entry deep, which would drop
      // this branch and send the user down the flow below instead. That flow is wrong here
      // twice over. It calls into services/office, and a dialog has no document API for those
      // clears to work through — the pane owns the deck, not this window. And it would unbind a
      // frame that is still presenting, which is exactly what the editor entrance promises not
      // to do (see ppt-addin-editor-routes).
      //
      // So Back in a dialog means what it means everywhere the user cannot go backwards: take
      // me to the list. The dialog stays open on it, which is the honest shape — the user came
      // to work on a presentation, and the list is where they choose another one.
      //
      // EMBED_MY_PRESENTATIONS is what pptAddinListRouteName resolves to under this same flag;
      // named directly rather than through the helper because the flag is already established
      // by the line above, so the helper's other branch is unreachable here.
      if (
        rootGetters[PPT_NEW_UI_FLAG]
        && isPptAddinEditorRoute(router.currentRoute.name)
        && isPptDialog()
      ) {
        router.push({ name: ROUTE_NAMES.EMBED_MY_PRESENTATIONS })
        return
      }
      if (
        rootGetters[PPT_NEW_UI_FLAG]
        && isPptAddinEditorRoute(router.currentRoute.name)
        && window.history.length > 1
      ) {
        router.back()
        return
      }
      // AHA-46192: forget the document's last-selected presentation before the reload,
      // otherwise the blank-frame fallback on /apps/presentations would re-open the
      // presentation the user is deliberately leaving and trap them. Dynamic import
      // avoids a circular dep (office.js imports the store); it's already cached here.
      const { default: office } = await import('@/services/office')
      await office.clearLastSelectedPresentation()
      // AHA-46329: and forget this frame's per-slide binding. The slide tag is the
      // highest-precedence source on the load path below (it has to be, to tell cloned
      // frames apart), so clearing settings + the last-selected tag is NOT enough: the
      // reload would read the leftover slide tag and route straight back into the
      // presentation being left. Unconditional on a host that has the tag API (AHA-46751):
      // the tag is written by every binding write, whatever the multi-select flag says.
      await office.clearSlideBindingForCurrentFrame()
      // AHA-47221: with the new add-in UI, Back stops being a special case — it hands over
      // to the ordinary editor Back flow, the same one every non-add-in editor uses. That is
      // what returns the frame to the embed list it was opened from (backV4Action pushes the
      // recorded lastDestination), without this module having to know that route's URL.
      //
      // Both clears above still run first: they are about this frame's PowerPoint bindings,
      // not about where the user goes next, and skipping them would let the leftover binding
      // pull the user straight back into the presentation they are leaving.
      //
      // Two things this deliberately gives up, both accepted for now:
      //   - the full reload. backV4Action does a router push, so the frame survives and
      //     services/office's cold-load chain does not re-run on the way out.
      //   - the add-in's exemption from backV4Action's other branches (guest user, team
      //     working mode, ?from=workspace), which a PowerPoint frame never reached before.
      //
      // Flag off => the original line below runs, unchanged.
      if (rootGetters[PPT_NEW_UI_FLAG]) {
        dispatch('backV4Action')
        return
      }
      window.location.href = router.resolve('/apps/presentations').href
      return
    }
    dispatch('backV4Action')
  },
  async backV2Action({ getters, rootState }) {
    if (getters.isGuestUser) {
      window.location.href = 'https://ahaslides.com/'
      return
    }
    if (getters.workingMode === 'team') {
      router.push('/apps/team/workspace')
      return
    }

    const { previousRoute } = getters
    const currentRouteName = rootState.route.name
    const { referrer } = document
    const hasHistory = window.history.length > 1
    const isDifferentSPARoute = previousRoute.name && previousRoute.name !== currentRouteName
    const isNoReferrer = referrer === ''
    const isExternalReferrer = referrer && new URL(referrer).origin !== config.presenterOrigin

    // Case 1: Back to previous SPA route
    if (isDifferentSPARoute) {
      window.history.back()
      return
    }

    // Case 2 & 3 combined: No referrer OR external referrer OR no meaningful history → Go to Presentations
    if (isNoReferrer || !hasHistory || isExternalReferrer) {
      router.push({ name: ROUTE_NAMES.PRESENTATIONS })
      return
    }

    // Case 4: Safe to go back
    window.history.back()
  },
  async backV4Action({ getters, rootState, rootGetters }) {
    if (getters.isGuestUser) {
      window.location.href = 'https://ahaslides.com/'
      return
    }
    // AHA-46604: the user opened this editor from the workspace app, which
    // does a full top-window navigation — so vuex previousRoute is wiped and
    // document.referrer looks external, sending the Case 2 fallback below to
    // the old My Presentations page. Honour the explicit `from=workspace`
    // signal (never trusted as provenance on its own — it survives the
    // auth-chain redirect and travels with copied links; see backToWorkspace
    // above for the guard set). Placed before the team-mode branch so it wins
    // in either working mode.
    const hasHistory = window.history.length > 1
    if (rootState.route?.query?.from === WORKSPACE_FROM_QUERY) {
      backToWorkspace(rootGetters, hasHistory, rootState.route.query)
      return
    }
    if (getters.workingMode === 'team') {
      router.push('/apps/team/workspace')
      return
    }

    // AHA-46714: push the recorded destination (see resolveBackDestination).
    // Placed after the branches above so guest, workspace and team back
    // behavior stays untouched.
    // The gate is negated on purpose: this is the default path, and the flag
    // is the rollback that hands control back to the legacy block below (see
    // canUseLegacyEditorBack). One consequence of that polarity: Unleash
    // flags load async, so a Back click fired before initUnleash() resolves
    // takes this path even while the rollback flag is on. Acceptable: the
    // window is the first moments of a page load, and the fallback of a
    // rollback switch should be the shipped behavior, not a stale legacy one.
    if (!rootGetters['featureToggles/canUseLegacyEditorBack']) {
      const path = resolveBackDestination(getters.lastDestination, rootState.currentUser.id)
      router.push(path || { name: ROUTE_NAMES.PRESENTATIONS })
      return
    }

    const { previousRoute } = getters
    const currentRoute = rootState.route
    const { referrer } = document
    // const isDifferentSPARoute = previousRoute.name && previousRoute.name !== currentRoute.name
    // The legacy code above didn't handle navigation inside one route with the same name
    // e.g. /presentation/123 -> /presentation/456 are consider the same route.
    // So I compare fullPath instead of name to correct this behavior
    const isDifferentSPARoute = previousRoute?.name &&
    previousRoute.fullPath !== currentRoute.fullPath
    const isExternalReferrer = referrer && new URL(referrer).origin !== config.presenterOrigin

    // Case 1: Back to previous SPA route
    if (isDifferentSPARoute) {
      if (isPreviousRouteTransitional(previousRoute.name)) {
        router.push({ name: ROUTE_NAMES.HOME })
      } else {
        window.history.back()
      }
      return
    }

    // Case 2 & 3 combined: No referrer OR external referrer OR no meaningful history → Go to Presentations
    if (isReferrerEmtpy() || !hasHistory || isExternalReferrer) {
      router.push({ name: ROUTE_NAMES.PRESENTATIONS })
      return
    }
    // Case 4: Safe to go back
    window.history.back()
  },
  setPreviousRouteAction({ commit }, route) {
    commit(SET_PREVIOUS_ROUTE, route)
  },
  setLastDestinationAction({ commit, rootState }, path) {
    // See isNestedSameOriginFrame: an embedded copy of this app must not own
    // the tab's shared Back target.
    if (isNestedSameOriginFrame()) return
    // Stamped here: actions see rootState, mutations do not. ?? null because
    // the first navigation of a page load fires before currentUser has loaded
    // and JSON.stringify would drop an undefined userId.
    commit(SET_LAST_DESTINATION, { userId: rootState.currentUser.id ?? null, path })
  },
  setBackstageRightColumnCollapsedAction({ commit }, isCollapsed) {
    commit('SET_BACKSTAGE_RIGHT_COLUMN_COLLAPSED', isCollapsed)
    // Dispatch resize event after transition
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 350)
  },
}

const mutations = {
  [SET_PREVIOUS_ROUTE](state, route) {
    state.previousRoute = route
  },
  [SET_LAST_DESTINATION](state, dest) {
    state.lastDestination = dest
    try {
      if (dest) {
        sessionStorage.setItem(EDITOR_LAST_DESTINATION, JSON.stringify(dest))
      } else {
        sessionStorage.removeItem(EDITOR_LAST_DESTINATION)
      }
    } catch (e) {
      // Best-effort mirror: storage-restricted contexts keep the vuex value
      // for this page load and lose it on reload, which is the legacy behavior.
    }
  },
  SET_BACKSTAGE_RIGHT_COLUMN_COLLAPSED(state, isCollapsed) {
    state.isBackstageRightColumnCollapsed = isCollapsed
    localStorage.setItem('isBackstageRightColumnCollapsed', isCollapsed)
  },
}

const editor = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}

export default editor
